import { useState, useMemo, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Key,
  Plus,
  Trash2,
  Copy,
  Check,
  AlertTriangle,
  Activity,
  Clock,
  Shield,
  Loader2,
  ArrowDownLeft,
  RefreshCw,
} from "lucide-react";
import { SearchInput } from "@/components/ui/search-input";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  platformApi,
  ExternalAPIKey,
  APIKeyScope,
  RateLimitTier,
} from "@/services/api/platform";
import { usePermissions } from "@/hooks/usePermissions";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";
import { SortableTableHead } from "@/components/ui/sortable-table-head";

export const Route = createFileRoute("/portal/platform/api-keys")({
  component: ExternalAPIKeysPage,
});

const AVAILABLE_SCOPES: {
  value: APIKeyScope;
  label: string;
  description: string;
}[] = [
  {
    value: "rag:query",
    label: "File Query",
    description: "Query knowledgebase files",
  },
  {
    value: "rag:ingest",
    label: "File Ingest",
    description: "Upload and process documents",
  },
  {
    value: "documents:read",
    label: "Documents Read",
    description: "Read document metadata",
  },
  {
    value: "documents:write",
    label: "Documents Write",
    description: "Create/update documents",
  },
  {
    value: "agentic:execute",
    label: "Agentic Execute",
    description: "Execute AI workflows",
  },
  {
    value: "mcp:query",
    label: "SQL RAG MCP",
    description: "Query SQL data sources from your own Claude via MCP",
  },
];

const RATE_LIMIT_TIERS: {
  value: RateLimitTier;
  label: string;
  description: string;
}[] = [
  { value: "free", label: "Free", description: "100 requests/day" },
  { value: "standard", label: "Standard", description: "10,000 requests/day" },
  { value: "enterprise", label: "Enterprise", description: "Unlimited" },
];

function getTierBadgeColor(tier: RateLimitTier): string {
  switch (tier) {
    case "free":
      return "bg-gray-100 text-gray-700";
    case "standard":
      return "bg-blue-100 text-blue-700";
    case "enterprise":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "Never";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ExternalAPIKeysPage() {
  const { token } = useAuth();
  const { selectedOrganisation, loading: orgLoading } = usePortalData();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTier, setFilterTier] = useState<string>("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState("");
  const [keyToRevoke, setKeyToRevoke] = useState<ExternalAPIKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // Form state
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState<APIKeyScope[]>([]);
  const [newKeyTier, setNewKeyTier] = useState<RateLimitTier>("standard");
  const [newKeyExpiry, setNewKeyExpiry] = useState("");

  const { can } = usePermissions();
  const canManageApiKeys = can("manage", "api-keys");
  const selectedOrgId = selectedOrganisation?.organisationId || "";

  // Server-side pagination
  const fetchFn = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<ExternalAPIKey>> => {
      if (!token || !selectedOrgId || !canManageApiKeys) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const response = await platformApi.listAPIKeys(token, selectedOrgId, {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          search: searchTerm || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load API keys:", error);
        toast.error("Failed to load API keys");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, selectedOrgId, canManageApiKeys, searchTerm, sortBy, sortOrder],
  );

  const {
    data: apiKeys,
    isLoading: keysLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<ExternalAPIKey>({
    fetchFn,
    initialPageSize: 25,
    dependencies: [token, selectedOrgId, canManageApiKeys, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  // Client-side tier filtering (within current page)
  const filteredKeys = useMemo(() => {
    return apiKeys.filter((key) => {
      const matchesSearch =
        searchTerm === "" ||
        key.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        key.key_prefix.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTier =
        filterTier === "all" || key.rate_limit_tier === filterTier;

      return matchesSearch && matchesTier;
    });
  }, [apiKeys, searchTerm, filterTier]);

  const stats = useMemo(() => {
    return {
      total: pagination.totalCount,
      active: apiKeys.filter((k) => k.is_active).length,
      totalUsage: apiKeys.reduce((sum, k) => sum + k.usage_count, 0),
    };
  }, [apiKeys, pagination.totalCount]);

  const handleCreateKey = async () => {
    if (!token || !selectedOrgId) return;
    if (!newKeyName.trim()) {
      toast.error("Please enter a key name");
      return;
    }
    if (newKeyScopes.length === 0) {
      toast.error("Please select at least one scope");
      return;
    }

    setCreating(true);
    try {
      const response = await platformApi.createAPIKey(token, selectedOrgId, {
        organisation_id: selectedOrgId,
        name: newKeyName,
        scopes: newKeyScopes,
        rate_limit_tier: newKeyTier,
        expires_at: newKeyExpiry || null,
      });
      setNewKeyValue(response.key);
      setShowCreateDialog(false);
      setShowKeyDialog(true);
      refresh();
      toast.success("API key created successfully");
      // Reset form
      setNewKeyName("");
      setNewKeyScopes([]);
      setNewKeyTier("standard");
      setNewKeyExpiry("");
    } catch (error) {
      console.error("Failed to create API key:", error);
      toast.error("Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!token || !selectedOrgId || !keyToRevoke) return;
    setRevoking(true);
    try {
      await platformApi.revokeAPIKey(token, selectedOrgId, keyToRevoke.id);
      toast.success("API key revoked successfully");
      setShowRevokeDialog(false);
      setKeyToRevoke(null);
      refresh();
    } catch (error) {
      console.error("Failed to revoke API key:", error);
      toast.error("Failed to revoke API key");
    } finally {
      setRevoking(false);
    }
  };

  const handleCopyKey = async () => {
    await navigator.clipboard.writeText(newKeyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("API key copied to clipboard");
  };

  const toggleScope = (scope: APIKeyScope) => {
    setNewKeyScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  if (!canManageApiKeys) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
        <Shield className="mx-auto h-12 w-12 text-slate-300" />
        <p className="mt-4 text-slate-600">
          API key management requires admin or owner access.
        </p>
      </div>
    );
  }

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!selectedOrgId) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
        <Key className="mx-auto h-12 w-12 text-slate-300" />
        <p className="mt-4 text-slate-600">
          Select an organisation from the header to manage API keys.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 flex items-center gap-3">
            <ArrowDownLeft className="h-8 w-8" />
            External API keys
          </h1>
          <p className="mt-2 text-lg text-slate-600">
            <span className="font-medium">Inbound</span> — keys that let
            external systems call the Larasoft API on behalf of{" "}
            {selectedOrganisation?.organisationName || "your organisation"}. For
            outbound credentials your workflows use to call other services, see{" "}
            <Link
              to="/portal/platform/secrets"
              className="text-brand-blue-medium underline hover:text-brand-blue-dark"
            >
              Integration secrets
            </Link>
            .
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="rounded-full">
          <Plus className="h-4 w-4 mr-2" />
          New key
        </Button>
      </div>

      {selectedOrgId && (
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Keys
                </CardTitle>
                <Key className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  API keys created
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Keys
                </CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.active}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Currently active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Requests
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.totalUsage.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Lifetime API calls
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by name or prefix…"
              containerClassName="flex-1 min-w-[200px]"
            />
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                {RATE_LIMIT_TIERS.map((tier) => (
                  <SelectItem key={tier.value} value={tier.value}>
                    {tier.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={refresh}>
              {keysLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>

          {keysLoading && filteredKeys.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredKeys.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No API keys found</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create your first API key
                  </Button>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead direction={getSortDirection("name")} onSort={() => handleSort("name")}>Name</SortableTableHead>
                        <SortableTableHead direction={getSortDirection("key_prefix")} onSort={() => handleSort("key_prefix")}>Prefix</SortableTableHead>
                        <SortableTableHead direction={null} onSort={() => {}} disabled>Scopes</SortableTableHead>
                        <SortableTableHead direction={getSortDirection("rate_limit_tier")} onSort={() => handleSort("rate_limit_tier")}>Tier</SortableTableHead>
                        <SortableTableHead direction={getSortDirection("usage_count")} onSort={() => handleSort("usage_count")}>Usage</SortableTableHead>
                        <SortableTableHead direction={getSortDirection("last_used_at")} onSort={() => handleSort("last_used_at")}>Last Used</SortableTableHead>
                        <SortableTableHead direction={getSortDirection("is_active")} onSort={() => handleSort("is_active")}>Status</SortableTableHead>
                        <SortableTableHead direction={null} onSort={() => {}} disabled>Actions</SortableTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredKeys.map((key) => (
                        <TableRow key={key.id}>
                          <TableCell className="font-medium">
                            {key.name}
                          </TableCell>
                          <TableCell>
                            <code className="bg-muted px-2 py-1 rounded text-sm">
                              {key.key_prefix}...
                            </code>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {key.scopes.slice(0, 2).map((scope) => (
                                <Badge
                                  key={scope}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {scope}
                                </Badge>
                              ))}
                              {key.scopes.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{key.scopes.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={getTierBadgeColor(key.rate_limit_tier)}
                            >
                              {key.rate_limit_tier}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {key.usage_count.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(key.last_used_at)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={key.is_active ? "success" : "destructive"}
                            >
                              {key.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                setKeyToRevoke(key);
                                setShowRevokeDialog(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="mt-4">
                    <Pagination
                      page={pagination.page}
                      totalPages={pagination.totalPages}
                      totalCount={pagination.totalCount}
                      pageSize={pagination.pageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      canGoPrev={canGoPrev}
                      canGoNext={canGoNext}
                      onPageChange={goToPage}
                      onPageSizeChange={setPageSize}
                      onFirstPage={firstPage}
                      onLastPage={lastPage}
                      onPrevPage={prevPage}
                      onNextPage={nextPage}
                    />
                  </div>
                </>
              )}
        </div>
      )}

      {/* Create Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create External API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for 3rd party integrations. The key will only
              be shown once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Key Name</Label>
              <Input
                id="key-name"
                placeholder="e.g., Production Integration"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Scopes (Permissions)</Label>
              <div className="space-y-2 border rounded-md p-3">
                {AVAILABLE_SCOPES.map((scope) => (
                  <div key={scope.value} className="flex items-start space-x-3">
                    <Checkbox
                      id={scope.value}
                      checked={newKeyScopes.includes(scope.value)}
                      onCheckedChange={() => toggleScope(scope.value)}
                    />
                    <div className="space-y-0.5">
                      <Label
                        htmlFor={scope.value}
                        className="cursor-pointer font-medium"
                      >
                        {scope.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {scope.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate-limit-tier">Rate Limit Tier</Label>
              <Select
                value={newKeyTier}
                onValueChange={(v) => setNewKeyTier(v as RateLimitTier)}
              >
                <SelectTrigger id="rate-limit-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RATE_LIMIT_TIERS.map((tier) => (
                    <SelectItem key={tier.value} value={tier.value}>
                      <span className="font-medium">{tier.label}</span>
                      <span className="text-muted-foreground ml-2">
                        ({tier.description})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiry">Expiration (Optional)</Label>
              <Input
                id="expiry"
                type="datetime-local"
                value={newKeyExpiry}
                onChange={(e) => setNewKeyExpiry(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty for no expiration
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateKey} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Key"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Save Your API Key
            </DialogTitle>
            <DialogDescription>
              This is the only time you will see this key. Copy it now and store
              it securely.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-muted rounded-md p-4 font-mono text-sm break-all">
              {newKeyValue}
            </div>
            <Button
              variant="outline"
              className="w-full mt-3"
              onClick={handleCopyKey}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy to Clipboard
                </>
              )}
            </Button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
            <strong>Warning:</strong> This key grants access to your
            organisation&apos;s data. Never share it publicly or commit it to
            version control.
          </div>

          <DialogFooter>
            <Button onClick={() => setShowKeyDialog(false)}>
              I&apos;ve Saved the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Key Dialog */}
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke the API key &quot;
              {keyToRevoke?.name}&quot;? This action cannot be undone and will
              immediately invalidate the key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleRevokeKey}
              disabled={revoking}
            >
              {revoking ? "Revoking..." : "Revoke Key"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
